import type { CompanyFundingScenario, FundingAcquisitionTarget, FundingReservePolicy, SupplementalPersonalIncome } from "../domain/companyFunding";
import { validateCompanyFundingScenario } from "./companyFundingValidation";

/** Preflight only: period totals do not establish dated liquidity or borrowing feasibility. */
export interface PersonalFundingPeriod {
  from: string;
  through: string;
  spendableTargetCents: number | null;
  supplementalPersonalIncomeCents: number | null;
  personalSurplusCents: number | null;
  personalTaxReserveCents: number | null;
  companyFundedSpendableCents: number | null;
  companyFundingNeedCents: number | null;
  incomeOccurrences: Array<{ sourceId: string; date: string; amountCents: number | null }>;
}

const DAY = 86_400_000;
const iso = (date: Date): string => date.toISOString().slice(0, 10);

/** Always anchor to the original day: Jan 31 → Feb 28 → Mar 31, without drift. */
function anniversary(start: string, offset: number): string {
  const date = new Date(`${start}T00:00:00Z`);
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + offset);
  const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, last));
  return iso(date);
}

function cents(value: bigint): number {
  const result = Number(value);
  if (!Number.isSafeInteger(result)) throw new Error("Planning total exceeds exact-cent limits.");
  return result;
}

function sum(values: readonly number[]): number {
  return cents(values.reduce((total, value) => total + BigInt(value), 0n));
}

function percent(amount: number, pctX100: number): number {
  return cents((BigInt(amount) * BigInt(pctX100) + 5_000n) / 10_000n);
}

function occurrences(income: SupplementalPersonalIncome, from: string, through: string, variant: "expected" | "conservative"): PersonalFundingPeriod["incomeOccurrences"] {
  const result: PersonalFundingPeriod["incomeOccurrences"] = [];
  const end = income.endDate && income.endDate < through ? income.endDate : through;
  const first = income.cadence === "weekly" ? Math.max(0, Math.ceil((Date.parse(`${from}T00:00:00Z`) - Date.parse(`${income.startDate}T00:00:00Z`)) / (7 * DAY)))
    : income.cadence === "monthly" ? Math.max(0, (Number(from.slice(0, 4)) - Number(income.startDate.slice(0, 4))) * 12 + Number(from.slice(5, 7)) - Number(income.startDate.slice(5, 7)) - 1) : 0;
  for (let index = first; ; index++) {
    const date = income.cadence === "monthly" ? anniversary(income.startDate, index)
      : income.cadence === "weekly" ? iso(new Date(Date.parse(`${income.startDate}T00:00:00Z`) + index * 7 * DAY))
      : income.startDate;
    if (date > end) break;
    if (date >= from) result.push({ sourceId: income.id, date, amountCents: variant === "expected" ? income.expectedCents : income.conservativeCents });
    if (income.cadence === "dated") break;
  }
  return result;
}

export interface DatedPersonalFundingRequirement {
  id: string;
  date: string;
  /** Unknown remains null and blocks certification. */
  amountCents: number | null;
  label: string;
  spendableTargetCents: number | null;
  taxReserveCents: number | null;
  supplementalUsedCents: number | null;
  personalReserveUsedCents: number | null;
  companyFundedSpendableCents: number | null;
  companyFundedTaxReserveCents: number | null;
  sourceIncomeIds: string[];
}
export interface PersonalCashPoint {
  date: string;
  personalCashCents: number | null;
  companyFundingCents: number | null;
  /** Explicit reserve enables inter-month carry; otherwise this is same-month income only. */
  carryEnabled: boolean;
}

function calendarMonthStart(start: string, offset: number): string {
  const date=new Date(`${start}T00:00:00Z`);date.setUTCDate(1);date.setUTCMonth(date.getUTCMonth()+offset);return iso(date);
}
function priorDay(date:string):string{return iso(new Date(Date.parse(`${date}T00:00:00Z`)-DAY));}
function splitCents(amount:number|null,count:number):Array<number|null>{
  if(amount===null)return Array.from({length:count},()=>null);
  const base=BigInt(amount)/BigInt(count),remainder=BigInt(amount)%BigInt(count);
  return Array.from({length:count},(_,index)=>cents(base+(BigInt(index)<remainder?1n:0n)));
}

/**
 * Dated personal-needs model. New scenarios use calendar-month weekly availability.
 * The first partial month retains the full entered monthly target, explicitly unprorated.
 * Without an explicit personal balance, income may bridge later needs within its calendar
 * month but is never carried across a month boundary or used before its availability date.
 */
export function datedPersonalFundingNeeds(scenario:CompanyFundingScenario,variant:"expected"|"conservative"="expected") {
  const validation=validateCompanyFundingScenario(scenario),issues=[...validation.issues];
  const requirements:DatedPersonalFundingRequirement[]=[],personalCashPoints:PersonalCashPoint[]=[],periods:PersonalFundingPeriod[]=[];
  const timing=scenario.target?.timing;
  const timingExplanation=timing===undefined?"Legacy saved plan: full amount at each original monthly anniversary."
    :timing==="weekly"?"Weekly dates start on the first covered day of each calendar month, then every seven days. A partial first month uses the full monthly target; no silent proration."
    :timing==="custom"?"Explicit dated personal requirements replace the monthly template."
    :"Full monthly amount on the first covered day of each calendar month; a partial first month uses the full monthly target.";
  if(!validation.valid)return{planningOnly:true as const,status:"inputs-incomplete" as const,requirements,periods,personalCashPoints,totalCompanyFundingNeedCents:null,issues,companyCashFromSupplementalCents:0 as const,timingExplanation};
  const custom=[...(scenario.target.customRequirements??[])].sort((a,b)=>a.date.localeCompare(b.date)||a.id.localeCompare(b.id));
  const periodDates=timing==="custom"?[...new Set(custom.map(item=>item.date.slice(0,7)))].map(month=>({from:`${month}-01`,through:priorDay(calendarMonthStart(`${month}-01`,1))}))
    :Array.from({length:scenario.target.durationMonths},(_,index)=>timing===undefined
      ?{from:anniversary(scenario.target.startDate,index),through:priorDay(anniversary(scenario.target.startDate,index+1))}
      :{from:index===0?scenario.target.startDate:calendarMonthStart(scenario.target.startDate,index),through:priorDay(calendarMonthStart(scenario.target.startDate,index+1))});
  const reserve=scenario.personalCashReserve;
  const carryEnabled=!!reserve;
  const reserveConfirmed=!!reserve&&reserve.confirmed&&reserve.balanceCents!==null&&!!reserve.evidenceReference&&reserve.asOfDate===scenario.asOfDate;
  let openingReserve:number|null=reserve?reserveConfirmed?reserve.balanceCents:null:0;
  let availableReserve=openingReserve, availableIncome:number|null=0;
  let previousThrough:string|null=null;
  let pendingIncome:Array<{sourceId:string;date:string;remaining:number|null}>=[];
  for(let index=0;index<periodDates.length;index++){
    const {from,through}=periodDates[index];
    if(!carryEnabled){availableIncome=0;pendingIncome=[];}
    const incomeFrom=carryEnabled?(previousThrough?iso(new Date(Date.parse(`${previousThrough}T00:00:00Z`)+DAY)):scenario.asOfDate)
      :timing===undefined?from:(calendarMonthStart(from,0)>scenario.asOfDate?calendarMonthStart(from,0):scenario.asOfDate);
    const incomeOccurrences=scenario.supplementalIncome.flatMap(income=>occurrences(income,incomeFrom,through,variant)).sort((a,b)=>a.date.localeCompare(b.date)||a.sourceId.localeCompare(b.sourceId));
    const supplemental=incomeOccurrences.some(item=>item.amountCents===null)?null:sum(incomeOccurrences.map(item=>item.amountCents!));
    let needs:Array<{id:string;date:string;spendable:number|null;tax:number|null}>;
    if(timing==="custom")needs=custom.filter(item=>item.date>=from&&item.date<=through).map(item=>({id:`personal:${item.id}`,date:item.date,spendable:item.spendableCents,tax:item.taxReserveCents}));
    else{
      const dates:string[]=[];
      for(let date=from;date<=through;date=iso(new Date(Date.parse(`${date}T00:00:00Z`)+7*DAY))){dates.push(date);if(timing!=="weekly")break;}
      const spendable=splitCents(scenario.target.monthlyCents,dates.length),tax=splitCents(scenario.target.personalTaxReserveMonthlyCents,dates.length);
      needs=dates.map((date,position)=>({id:`owner:${index}:${position}`,date,spendable:spendable[position],tax:tax[position]}));
    }
    let incomeIndex=0;
    const receiveThrough=(date:string)=>{
      while(incomeIndex<incomeOccurrences.length&&incomeOccurrences[incomeIndex].date<=date){
        const received=incomeOccurrences[incomeIndex++];
        availableIncome=availableIncome===null||received.amountCents===null?null:sum([availableIncome,received.amountCents]);
        pendingIncome.push({sourceId:received.sourceId,date:received.date,remaining:received.amountCents});
      }
    };
    const periodRequirements:DatedPersonalFundingRequirement[]=[];
    for(const need of needs){
      receiveThrough(need.date);
      let amount:number|null=null,incomeUsed:number|null=null,reserveUsed:number|null=null,companySpendable:number|null=null,companyTax:number|null=null;
      const sourceIncomeIds:string[]=[];
      if(need.spendable!==null&&need.tax!==null&&availableIncome!==null&&availableReserve!==null){
        incomeUsed=Math.min(need.spendable,availableIncome);availableIncome-=incomeUsed;
        let toAllocate=incomeUsed;
        for(const item of pendingIncome){
          if(toAllocate<=0)break;
          const used=Math.min(item.remaining??0,toAllocate);if(used>0){sourceIncomeIds.push(item.sourceId);item.remaining=(item.remaining??0)-used;toAllocate-=used;}
        }
        const afterIncome=need.spendable-incomeUsed;
        const spendableReserve=Math.min(afterIncome,availableReserve);availableReserve-=spendableReserve;
        const taxReserve=Math.min(need.tax,availableReserve);availableReserve-=taxReserve;
        reserveUsed=sum([spendableReserve,taxReserve]);companySpendable=afterIncome-spendableReserve;companyTax=need.tax-taxReserve;amount=sum([companySpendable,companyTax]);
      }
      const row:DatedPersonalFundingRequirement={id:need.id,date:need.date,amountCents:amount,label:"Personal cash and separate personal tax reserve",spendableTargetCents:need.spendable,taxReserveCents:need.tax,supplementalUsedCents:incomeUsed,personalReserveUsedCents:reserveUsed,companyFundedSpendableCents:companySpendable,companyFundedTaxReserveCents:companyTax,sourceIncomeIds:[...new Set(sourceIncomeIds)]};
      requirements.push(row);periodRequirements.push(row);
      personalCashPoints.push({date:need.date,personalCashCents:availableIncome===null||availableReserve===null?null:sum([availableIncome,availableReserve]),companyFundingCents:amount,carryEnabled});
    }
    receiveThrough(through);
    const target=needs.some(need=>need.spendable===null)?null:sum(needs.map(need=>need.spendable!));
    const tax=needs.some(need=>need.tax===null)?null:sum(needs.map(need=>need.tax!));
    const funded=periodRequirements.some(need=>need.companyFundedSpendableCents===null)?null:sum(periodRequirements.map(need=>need.companyFundedSpendableCents!));
    const companyNeed=periodRequirements.some(need=>need.amountCents===null)?null:sum(periodRequirements.map(need=>need.amountCents!));
    periods.push({from,through,spendableTargetCents:target,supplementalPersonalIncomeCents:supplemental,personalSurplusCents:availableIncome,personalTaxReserveCents:tax,companyFundedSpendableCents:funded,companyFundingNeedCents:companyNeed,incomeOccurrences});
    previousThrough=through;
  }
  if(requirements.some(item=>item.amountCents===null))issues.push({path:"personalCash",code:"personal-cash-unresolved",message:"Dated personal income, tax requirements or opening personal cash remain unresolved.",severity:"confirmation"});
  return{planningOnly:true as const,status:issues.length?"inputs-incomplete" as const:"ready-for-evaluation" as const,requirements,periods,personalCashPoints,
    totalCompanyFundingNeedCents:requirements.some(item=>item.amountCents===null)?null:sum(requirements.map(item=>item.amountCents!)),issues,companyCashFromSupplementalCents:0 as const,timingExplanation};
}

/** Legacy plans keep anniversary dates; displayed totals always reconcile to the dated requirements. */
export function companyFundingNeeds(scenario:CompanyFundingScenario,variant:"expected"|"conservative"="expected"){
  return datedPersonalFundingNeeds(scenario,variant);
}

/** A project-specific amount wins over percentage policy. Suggestions remain labelled. */
export function constructionContingencyRequirement(remainingCents: number | null, policy: FundingReservePolicy, projectId: string, projectSpecificCents: number | null = null) {
  if (projectSpecificCents !== null) {
    if (!Number.isSafeInteger(projectSpecificCents) || projectSpecificCents < 0) throw new Error("Invalid project contingency.");
    return { amountCents: projectSpecificCents, source: "project-specific" as const };
  }
  if (remainingCents !== null && (!Number.isSafeInteger(remainingCents) || remainingCents < 0)) throw new Error("Invalid remaining renovation cost.");
  const override = policy.projectOverrides.find(item => item.projectId === projectId);
  const rate = override?.contingencyPctX100 ?? policy.constructionContingencyPctX100;
  if (!Number.isSafeInteger(rate) || rate < 0 || rate > 10_000) throw new Error("Invalid contingency percentage.");
  return { amountCents: remainingCents === null ? null : percent(remainingCents, rate), source: override ? "project-policy" as const : policy.contingencyPolicySource };
}

export function acquisitionCashRequirement(target: FundingAcquisitionTarget): number | null {
  const { purchasePriceCents: price, downPaymentPctX100: rate, closingCostsCents: closing, requiredReservesCents: reserve } = target;
  if (price === null || rate === null || closing === null || reserve === null) return null;
  if (![price, closing, reserve].every(value => Number.isSafeInteger(value) && value >= 0) || !Number.isSafeInteger(rate) || rate < 0 || rate > 10_000) throw new Error("Invalid acquisition cash requirement.");
  return sum([percent(price, rate), closing, reserve]);
}
